import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { hasBorrowingBaseMechanics as hasBorrowingBaseMechanicsUtil } from '~/packages/common-frontend/utils';
import { CreditFacilityDocumentsHistoryAndUploadContainer } from '~/packages/credit-facility/ui/DocumentUpload/CreditFacilityDocumentsHistoryAndUploadContainer';
import {
  useGetCreditAgreementFullArtifactQuery,
  useGetCurrentCreditAgreementFullArtifactQuery,
} from '~/packages/creditAgreements/requests/requests.gql.generated';
import { BorrowingBaseMechanics } from '~/packages/creditAgreements/ui/CreditAgreementArtifact/sections/BorrowingBaseMechanics';
import { ReportingDeliverables } from '~/packages/creditAgreements/ui/CreditAgreementArtifact/sections/ReportingDeliverables';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementContextProvider } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks';
import { FacilityMechanics } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FacilityMechanics';
import { FinancialCovenants } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FinancialCovenants';
import { FundingRequestMechanics } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FundingRequestMechanics';
import { LoanMechanics } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/section/LoanMechanics';
import { PortfolioCovenants } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/section/PortfolioCovenants';
import { SettlementMechanics } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/section/SettlementMechanics';

type PortfolioCreditAgreementProps = {
  creditAgreementId: string;
  creditFacilityId: string;
  orgSlug: string;
  organizationId: string;
};
const PortfolioCreditAgreement = ({
  creditAgreementId,
  creditFacilityId,
  orgSlug,
  organizationId,
}: PortfolioCreditAgreementProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { isSlsAgentLender } = useFeatureFlags();

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCreditAgreementFullArtifactQuery({
    variables: { creditAgreementId },
  });

  if (
    isCreditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.creditAgreementFullArtifact
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="portfolio-credit-agreement-loading"
      />
    );
  }

  const {
    creditAgreementFullArtifact: { creditAgreement, id },
  } = creditAgreementData;

  let parsedCreditAgreement = null;

  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(creditAgreement);
    finleyAssert(
      parsedCreditAgreement,
      `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
    );

    const {
      borrowingBaseMechanics,
      fundingRequestMechanics,
      facilityMechanics,
      settlementMechanics,
      financialCovenants,
      portfolioCovenants,
      reportingDeliverables,
      loanMechanicsV1,
    } = parsedCreditAgreement;

    const hasBorrowingBaseMechanics = hasBorrowingBaseMechanicsUtil(
      borrowingBaseMechanics,
    );

    const hasReportingDeliverables = reportingDeliverables.length > 0;

    return (
      <Box>
        <CreditAgreementContextProvider
          initOrgSlugs={[orgSlug]}
          parties={facilityMechanics.parties}
        >
          <CreditFacilityDocumentsHistoryAndUploadContainer
            creditFacilityId={creditFacilityId}
          />
          <Box h="36px" />
          <FacilityMechanics facilityMechanics={facilityMechanics} />
          {/* @VLYSLS @jendevelops - temp flag */}
          {!isSlsAgentLender && (
            <FundingRequestMechanics
              fundingRequestMechanics={fundingRequestMechanics}
            />
          )}
          <SettlementMechanics settlementMechanics={settlementMechanics} />
          {hasBorrowingBaseMechanics && !isSlsAgentLender && (
            <BorrowingBaseMechanics
              borrowingBaseMechanics={borrowingBaseMechanics}
            />
          )}
          <FinancialCovenants
            financialCovenants={financialCovenants}
            organizationId={organizationId}
          />
          <PortfolioCovenants
            portfolioCovenants={portfolioCovenants}
            organizationId={organizationId}
          />
          {hasReportingDeliverables && (
            <ReportingDeliverables
              reportingDeliverables={reportingDeliverables}
            />
          )}
          <LoanMechanics loanMechanics={loanMechanicsV1} />
        </CreditAgreementContextProvider>
      </Box>
    );
  } catch (err) {
    Sentry.captureException(err);
    errorToastConfig.title = `Error parsing credit agreement artifact for credit agreement ${id}`;
    toast(errorToastConfig);
    navigate('/');
  }
  throw new Error('unreachable code');
};

type PortfolioCreditAgreementContainerProps = {
  creditFacilityId: string;
  orgSlug: string;
  organizationId: string;
};
export const PortfolioCreditAgreementContainer = ({
  creditFacilityId,
  orgSlug,
  organizationId,
}: PortfolioCreditAgreementContainerProps) => {
  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
    fetchPolicy: 'network-only',
  });

  if (
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact ||
    isCreditAgreementLoading
  ) {
    return <Skeleton data-testid="loading-credit-agreement" height="600px" />;
  }

  const {
    currentCreditAgreementFullArtifact: { id: creditAgreementId },
  } = creditAgreementData;

  return (
    <PortfolioCreditAgreement
      creditAgreementId={creditAgreementId}
      creditFacilityId={creditFacilityId}
      orgSlug={orgSlug}
      organizationId={organizationId}
    />
  );
};
